// Pure logic for opportunities (saved Analyze scenarios).
//
// Sections are saved as arranged rather than as the demo's flat change list, so
// reopening is a lookup that keeps their order. `scope` holds the dates that were
// on screen, not the relative default, so a reopened opportunity shows the window
// it was analyzed in.

import { colorPalette, semanticColors } from '../config/colorPalette';
import { scopeToOpportunityFields } from './analysisScope';
import {
  buildCurrentMapping,
  buildProposedMapping,
  changeRows,
  createSection,
  moveRowToSection,
} from './analysisSections';
import { applyQuickDateRange, formatDateRangeDisplay, formatDisplayDate } from './dateUtils';
import { formulationsEquivalent } from './formulationString';
import { rtuCategoryLabel } from './rtuCategory';

export const OPPORTUNITY_STATUS = {
  SAVED: 'saved',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  RECOMMENDED: 'recommended',
  DISMISSED: 'dismissed',
};

// In status-picker order. Hex from the formulary palette, not theme names: the
// portal theme's info/success/warning are lighter than the demo's.
export const OPPORTUNITY_STATUSES = [
  { value: OPPORTUNITY_STATUS.ACTIVE, label: 'Active', color: semanticColors.success },
  { value: OPPORTUNITY_STATUS.SAVED, label: 'Saved', color: semanticColors.info },
  { value: OPPORTUNITY_STATUS.COMPLETED, label: 'Completed', color: 'teal' },
  { value: OPPORTUNITY_STATUS.RECOMMENDED, label: 'Recommended', color: semanticColors.warning },
  { value: OPPORTUNITY_STATUS.DISMISSED, label: 'Dismissed', color: 'grey' },
];

// Recommended opportunities have their own section on Manage, so `all` excludes them.
export const ALL_TAB = 'all';
export const OPPORTUNITY_TABS = [
  { value: OPPORTUNITY_STATUS.SAVED, label: 'Saved', color: semanticColors.info },
  { value: OPPORTUNITY_STATUS.ACTIVE, label: 'Active', color: semanticColors.success },
  { value: OPPORTUNITY_STATUS.COMPLETED, label: 'Completed', color: 'teal' },
  { value: OPPORTUNITY_STATUS.DISMISSED, label: 'Dismissed', color: 'grey' },
  { value: ALL_TAB, label: 'All', color: colorPalette.darkBlue },
];

const statusEntry = (status) => OPPORTUNITY_STATUSES.find((s) => s.value === status);

export function statusLabel(status) {
  return statusEntry(status)?.label || status || '';
}

export function statusColor(status) {
  return statusEntry(status)?.color || 'grey';
}

export function isDeletable(opportunity) {
  return opportunity?.status === OPPORTUNITY_STATUS.SAVED;
}

export function sortByRecent(opportunities) {
  const time = (value) => {
    const parsed = Date.parse(value || '');
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  return [...(opportunities || [])].sort((a, b) => time(b.updatedAt || b.createdAt) - time(a.updatedAt || a.createdAt));
}

export function opportunitiesForTab(opportunities, tab) {
  return (opportunities || []).filter((opportunity) => {
    if (opportunity.status === OPPORTUNITY_STATUS.RECOMMENDED) return false;
    return tab === ALL_TAB || opportunity.status === tab;
  });
}

export function countByTab(opportunities) {
  const counts = {};
  OPPORTUNITY_TABS.forEach(({ value }) => {
    counts[value] = opportunitiesForTab(opportunities, value).length;
  });
  return counts;
}

export function formatMedicationName(name) {
  return String(name || '')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatDateTime(iso) {
  const parsed = Date.parse(iso || '');
  if (Number.isNaN(parsed)) return '';
  const date = new Date(parsed);
  return `${formatDisplayDate(date)} ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

// An empty list filter was unfiltered when saved, so it reads as All. Facility
// names are shown as stored ("UCSF", not "Ucsf"); populations are ADULT-style codes.
export function scopeSummary(scope) {
  const list = (values, format = (v) => v) => (values && values.length ? values.map(format).join(', ') : 'All');
  const range = scope?.dateRange;
  return {
    dateRange: range?.start && range?.end ? formatDateRangeDisplay(range.start, range.end) : 'Not recorded',
    facilities: list(scope?.facilities),
    patientPopulations: list(scope?.patientPopulations, formatMedicationName),
    minInfusions: scope?.minInfusions ?? null,
  };
}

// The tenant user list is admin-only, so owners are whoever is already on the
// record, viewer first.
export function ownerCandidates(opportunity, me) {
  const others = new Set(
    [opportunity?.owner, opportunity?.createdBy, ...(opportunity?.notes || []).map((n) => n.author)].filter(
      (person) => person && person.includes('@') && person !== me
    )
  );
  return [...(me ? [me] : []), ...[...others].sort()];
}

export function defaultOpportunityTitle(medicationName) {
  return `Optimize ${formatMedicationName(medicationName)} Formulation(s)`;
}

const finiteOrNull = (value) => (Number.isFinite(value) ? value : null);

const savedRow = (row) => ({
  formulation: row.formulation,
  infusions: finiteOrNull(row.infusions),
  costPerInfusion: finiteOrNull(row.costPerInfusion),
});

// Untouched bucket rows are saved too, so Manage can show the whole resulting
// formulary and a cost typed on one survives reopening.
export function serializeAnalysis(analysisSections, currentFormulations) {
  return {
    sections: (analysisSections || [])
      .filter((section) => !!section.targetFormulation)
      .map((section) => ({
        targetFormulation: section.targetFormulation,
        targetRtuCategory: section.targetRtuCategory || null,
        targetCost: finiteOrNull(section.targetCost),
        // Split targets have no metrics yet, but are kept so reopening shows them.
        splitMode: section.splitMode || 'single',
        targetFormulation2: section.targetFormulation2 || '',
        targetCost2: finiteOrNull(section.targetCost2),
        rows: section.rows.map(savedRow),
      })),
    unchangedFormulations: (currentFormulations || []).map(savedRow),
  };
}

export function hasAnalysisChanges(analysisSections) {
  return changeRows(analysisSections || []).length > 0;
}

export function proposedChangeLines(analysisSections, formatFormulation = (f) => f) {
  return changeRows(analysisSections || []).map(({ section, row }) => {
    const rtu = rtuCategoryLabel(section.targetRtuCategory);
    const suffix = rtu ? ` (${rtu})` : '';
    return `Update ${formatFormulation(row.formulation)} to ${formatFormulation(section.targetFormulation)}${suffix}`;
  });
}

// Status, owner, notes and timestamps are left out: create defaults them and an
// update must not overwrite them.
export function buildOpportunityFields({
  title,
  description,
  medicationName,
  scope,
  concentrationUnit,
  analysisSections,
  currentFormulations,
  estimatedImpacts,
  formatFormulation,
}) {
  return {
    title: String(title || '').trim(),
    description: String(description || '').trim(),
    medicationName: medicationName || '',
    scope: scopeToOpportunityFields(scope || {}),
    concentrationUnit: concentrationUnit || null,
    analysis: serializeAnalysis(analysisSections, currentFormulations),
    proposedChanges: proposedChangeLines(analysisSections, formatFormulation),
    estimatedImpacts: estimatedImpacts || null,
  };
}

// Keeps the saved dates, but drops a preset label ("last180days") that no longer
// produces them: the date control shows the label and locks its inputs under it.
export function scopeForReopen(scope) {
  const range = scope?.dateRange || {};
  const preset = range.quickRange ? applyQuickDateRange(range.quickRange) : null;
  const stillPreset = !!preset && preset.startDate === range.start && preset.endDate === range.end;
  return {
    ...scope,
    dateRange: { ...range, quickRange: stillPreset ? range.quickRange : 'custom' },
  };
}

// Exact match first; the unit-normalized match covers a source that changed units.
const claimIndex = (currentFormulations, formulation) => {
  const exact = currentFormulations.findIndex((r) => r.formulation === formulation);
  if (exact !== -1) return exact;
  return currentFormulations.findIndex((r) => formulationsEquivalent(r.formulation, formulation));
};

// Mutates `currentFormulations`, moving saved rows into the rebuilt sections.
// Rows no longer in the data are returned in `missing`; their sections still
// come back.
export function restoreAnalysis(analysis, currentFormulations, nextSectionId) {
  const missing = [];
  const sections = (analysis?.sections || [])
    .filter((saved) => !!saved.targetFormulation)
    .map((saved) => {
      const section = createSection(nextSectionId());
      section.targetFormulation = saved.targetFormulation;
      section.targetRtuCategory = saved.targetRtuCategory || null;
      section.targetCost = finiteOrNull(saved.targetCost);
      section.splitMode = saved.splitMode === 'split' ? 'split' : 'single';
      section.targetFormulation2 = saved.targetFormulation2 || '';
      section.targetCost2 = finiteOrNull(saved.targetCost2);

      (saved.rows || []).forEach((savedRowEntry) => {
        const index = claimIndex(currentFormulations, savedRowEntry.formulation);
        if (index === -1) {
          missing.push(savedRowEntry.formulation);
          return;
        }
        const row = moveRowToSection(currentFormulations, section, currentFormulations[index].id);
        if (Number.isFinite(savedRowEntry.costPerInfusion)) row.costPerInfusion = savedRowEntry.costPerInfusion;
      });
      return section;
    });

  // Untouched rows stay in the bucket; only their typed cost is restored.
  (analysis?.unchangedFormulations || []).forEach((saved) => {
    if (!Number.isFinite(saved.costPerInfusion)) return;
    const index = claimIndex(currentFormulations, saved.formulation);
    if (index !== -1) currentFormulations[index].costPerInfusion = saved.costPerInfusion;
  });

  return { sections, missing };
}

// Same mapping builders as Analyze, so Manage's tables can't disagree with it.
export function savedMappings(analysis) {
  let nextId = 1;
  const withId = (row) => ({ ...row, id: nextId++ });
  const sections = (analysis?.sections || []).map((saved) => ({
    ...createSection(nextId++),
    targetFormulation: saved.targetFormulation,
    targetRtuCategory: saved.targetRtuCategory || null,
    rows: (saved.rows || []).map(withId),
  }));
  const bucket = (analysis?.unchangedFormulations || []).map(withId);
  return {
    currentMapping: buildCurrentMapping(sections, bucket),
    proposedMapping: buildProposedMapping(sections, bucket),
  };
}
